import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../theme/useChartTokens';
import { ReachCardSeriesPoint } from './types';

interface ReachTrendChartProps {
  series: ReachCardSeriesPoint[];
}

const formatAxisValue = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

// Same recharts conventions (grid/axis/tooltip tokens, gradient area fill) as
// CombinedPlaylistReachPanel and PlaylistScoreChart, so all Music24 charts
// read as one family.
const ReachTrendChartComponent: React.FC<ReachTrendChartProps> = ({ series }) => {
  const intl = useIntl();
  const chart = useChartTokens();

  const formatDate = (dateString: string, withYear = false): string =>
    new Date(dateString).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
    });

  const reachFollowersLabel = intl.formatMessage({
    id: 'app.sml.chart.reach-followers',
    defaultMessage: 'People reached',
  });

  const chartData = useMemo(() => series.map((point) => ({ ...point })), [series]);

  return (
    <div style={{ height: '200px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
          <defs>
            <linearGradient id="reachCardGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chart.brand} stopOpacity={0.15} />
              <stop offset="95%" stopColor={chart.brand} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: chart.label }}
            tickFormatter={(d: string) => formatDate(d)}
            axisLine={{ stroke: chart.grid }}
            tickLine={{ stroke: chart.grid }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: chart.axis }}
            axisLine={{ stroke: chart.grid }}
            tickLine={{ stroke: chart.grid }}
            tickFormatter={formatAxisValue}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: chart.tooltipBg,
              border: `1px solid ${chart.line}`,
              borderRadius: '8px',
              fontSize: '12px',
              color: chart.tooltipTx,
            }}
            labelFormatter={(d: string) => formatDate(d, true)}
            formatter={(value: number) => [value.toLocaleString(), reachFollowersLabel]}
          />
          <Area
            type="monotone"
            dataKey="reachFollowers"
            stroke={chart.brandLift}
            strokeWidth={2}
            fill="url(#reachCardGradient)"
            dot={{ fill: chart.brandLift, strokeWidth: 1, r: 3 }}
            activeDot={{ r: 5, stroke: chart.brandLift, strokeWidth: 1, fill: chart.dotCore }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const ReachTrendChart = TypedMemo(ReachTrendChartComponent);
